import React, { useState, useEffect } from 'react';
import Modal from '../../common/Modal';
import Input from '../../common/Input';
import Select from '../../common/Select';
import Button from '../../common/Button';
import { validateRequired } from '../../../utils/validators';
import customerService from '../../../services/customerService';
import siteService from '../../../services/siteService';
import systemTypeService from '../../../services/systemTypeService';

const AddContractModal = ({ isOpen, onClose, onSubmit }) => {
  const initialState = {
    company_id: '',
    site_id: '',
    po_number: '',
    value: '',
    type: 'CAMC',
    start_date: '',
    end_date: '',
    services: {
      comprehensive: [],
      non_comprehensive: []
    }
  };

  const [formData, setFormData] = useState(initialState);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  
  const [companies, setCompanies] = useState([]);
  const [sites, setSites] = useState([]);
  const [loadingCompanies, setLoadingCompanies] = useState(false);
  const [loadingSites, setLoadingSites] = useState(false);
  const [availableServices, setAvailableServices] = useState([]);

  useEffect(() => {
  systemTypeService.getProblemMapping().then(result => {
    if (result.success) setAvailableServices(Object.keys(result.data));
  });
  }, []);

  useEffect(() => {
    if (isOpen) {
      loadCompanies();
    }
  }, [isOpen]);

  useEffect(() => {
    if (formData.company_id) {
      loadSites(formData.company_id);
    } else {
      setSites([]); 
    }
  }, [formData.company_id]);

  // Auto-calculate contract type based on services
  useEffect(() => {
    const hasComp = formData.services.comprehensive.length > 0;
    const hasNonComp = formData.services.non_comprehensive.length > 0;
    
    let newType = 'CAMC'; // Default
    if (hasComp && hasNonComp) {
      newType = 'Both';
    } else if (hasComp) {
      newType = 'CAMC';
    } else if (hasNonComp) {
      newType = 'NCAMC';
    }
    
    if (formData.type !== newType) {
      setFormData(prev => ({ ...prev, type: newType }));
    }
  }, [formData.services]);

  const loadCompanies = async () => {
    setLoadingCompanies(true);
    const result = await customerService.getCustomerCompanies();
    if (result.success) setCompanies(result.data);
    setLoadingCompanies(false);
  };

  const loadSites = async (companyId) => {
    setLoadingSites(true);
    const result = await siteService.getAllSites();
    if (result.success) {
      const selectedCompanyId = parseInt(companyId, 10);
      const filteredSites = result.data.filter((site) => site.company_id === selectedCompanyId);
      setSites(filteredSites);
    }
    setLoadingSites(false);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => {
      const updatedData = { ...prev, [name]: value };
      if (name === 'company_id') {
        updatedData.site_id = '';
      }
      return updatedData;
    });

    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleServiceToggle = (category, service) => {
    setFormData(prev => {
      const currentServices = [...prev.services[category]];
      const updatedServices = currentServices.includes(service)
        ? currentServices.filter(s => s !== service)
        : [...currentServices, service];
        
      return { 
        ...prev, 
        services: {
          ...prev.services,
          [category]: updatedServices
        }
      };
    });
    if (errors.services) {
      setErrors(prev => ({ ...prev, services: '' }));
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!validateRequired(formData.company_id)) newErrors.company_id = 'Company is required';
    if (!validateRequired(formData.site_id)) newErrors.site_id = 'Site is required';
    if (!validateRequired(formData.po_number)) newErrors.po_number = 'PO Number is required';
    if (!validateRequired(formData.value)) newErrors.value = 'Contract Value is required';
    if (isNaN(formData.value) || parseFloat(formData.value) < 0) newErrors.value = 'Contract value must be a valid non-negative number';
    if (!validateRequired(formData.start_date)) newErrors.start_date = 'Start Date is required';
    if (!validateRequired(formData.end_date)) newErrors.end_date = 'End Date is required';
    
    if (new Date(formData.end_date) <= new Date(formData.start_date)) {
      newErrors.end_date = 'End Date must be after Start Date';
    }

    const totalServices = formData.services.comprehensive.length + formData.services.non_comprehensive.length;
    if (totalServices === 0) newErrors.services = 'At least one service must be selected';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      const payload = {
        ...formData,
        contract_id: formData.po_number,
        company_id: parseInt(formData.company_id),
        site_id: parseInt(formData.site_id),
        value: parseFloat(formData.value)
      };
      
      await onSubmit(payload);
      setFormData(initialState);
      onClose();
    } catch (error) {
      console.error('Error creating Contract:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!submitting) {
      setFormData(initialState);
      setErrors({});
      onClose();
    }
  };

  const companyOptions = [
    { value: '', label: '-- Select Company --' },
    ...companies.map(c => ({ value: c.id.toString(), label: c.company_name }))
  ];

  const siteOptions = [
    { value: '', label: '-- Select Site --' },
    ...sites.map(s => ({ value: s.id.toString(), label: s.name }))
  ];

  const gridStyle = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
    gap: '16px',
    marginBottom: '16px'
  };

  const footer = (
    <>
      <Button variant="secondary" onClick={handleClose} disabled={submitting}>Cancel</Button>
      <Button variant="primary" onClick={handleSubmit} disabled={submitting}>
        {submitting ? 'Adding...' : 'Add Contract'}
      </Button>
    </>
  );

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Add New Contract" size="large" footer={footer}>
      <form onSubmit={handleSubmit}>
        <div style={gridStyle}>
          <Select
            label="Company"
            name="company_id"
            value={formData.company_id}
            onChange={handleChange}
            options={companyOptions}
            error={errors.company_id}
            disabled={loadingCompanies}
            required
          />
          <Select
            label="Site"
            name="site_id"
            value={formData.site_id}
            onChange={handleChange}
            options={siteOptions}
            error={errors.site_id}
            disabled={!formData.company_id || loadingSites}
            required
          />
        </div>

        <div style={gridStyle}>
          <Input 
            label="PO Number" 
            name="po_number" 
            placeholder="e.g., PO-2026-001" 
            value={formData.po_number} 
            onChange={handleChange} 
            error={errors.po_number} 
            required 
          />
          <Input 
            label="Contract Value (₹)" 
            name="value" 
            type="number"
            placeholder="e.g., 150000" 
            value={formData.value} 
            onChange={handleChange} 
            error={errors.value} 
            required 
          />
        </div>

        <div style={gridStyle}>
          <Input
            label="Contract Type (Auto-calculated)"
            name="type"
            value={formData.type}
            disabled
            placeholder="Select services to determine type"
          />
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
            <Input 
              label="Start Date" 
              name="start_date" 
              type="date"
              value={formData.start_date} 
              onChange={handleChange} 
              error={errors.start_date} 
              required 
            />
            <Input 
              label="End Date" 
              name="end_date" 
              type="date"
              value={formData.end_date} 
              onChange={handleChange} 
              error={errors.end_date} 
              required 
            />
          </div>
        </div>

        <div style={{ marginTop: '16px' }}>
          <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: 'var(--text-primary)', marginBottom: '8px' }}>
            Services Covered <span style={{ color: 'red' }}>*</span>
          </label>
          
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            {/* Comprehensive Section */}
            <div>
              <h5 style={{ fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '8px', borderBottom: '1px solid var(--border)', paddingBottom: '4px' }}>Comprehensive Services</h5>
              <div style={{
                display: 'grid',
                gridTemplateColumns: '1fr',
                gap: '8px',
                padding: '12px',
                background: 'rgba(16, 185, 129, 0.05)',
                borderRadius: '8px',
                border: errors.services ? '1px solid #ef4444' : '1px solid var(--border)'
              }}>
                {availableServices.map(service => (
                  <label key={`comp-${service}`} style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                    <input 
                      type="checkbox" 
                      checked={formData.services.comprehensive.includes(service)}
                      onChange={() => handleServiceToggle('comprehensive', service)}
                      style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                    />
                    <span style={{ fontSize: '13px', color: 'var(--text-primary)' }}>{service}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Non-Comprehensive Section */}
            <div>
              <h5 style={{ fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '8px', borderBottom: '1px solid var(--border)', paddingBottom: '4px' }}>Non-Comprehensive Services</h5>
              <div style={{
                display: 'grid',
                gridTemplateColumns: '1fr',
                gap: '8px',
                padding: '12px',
                background: 'rgba(245, 158, 11, 0.05)',
                borderRadius: '8px',
                border: errors.services ? '1px solid #ef4444' : '1px solid var(--border)'
              }}>
                {availableServices.map(service => (
                  <label key={`noncomp-${service}`} style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                    <input 
                      type="checkbox" 
                      checked={formData.services.non_comprehensive.includes(service)}
                      onChange={() => handleServiceToggle('non_comprehensive', service)}
                      style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                    />
                    <span style={{ fontSize: '13px', color: 'var(--text-primary)' }}>{service}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>
          {errors.services && <span style={{ color: '#ef4444', fontSize: '12px', marginTop: '4px', display: 'block' }}>{errors.services}</span>}
        </div>
      </form>
    </Modal>
  );
};

export default AddContractModal;